import type { Screen0LanguageCode } from "@/features/language/language-registry";
import { getScreen0IntlLocale } from "@/features/language/language-registry";
import { formatTemplate, getTranslations } from "@/i18n";
import type { RuleWindow } from "./rules/ruleTypes";

const MINUTES_PER_DAY = 24 * 60;

function padClock(value: number): string {
  return String(value).padStart(2, "0");
}

/** User-facing clock time; exclusive end-of-day (1440) renders as 00:00. */
export function formatDisplayMinuteOfDay(minute: number): string {
  const normalized = ((minute % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return `${padClock(Math.floor(normalized / 60))}:${padClock(normalized % 60)}`;
}

export function isFullDayRuleWindow(window: RuleWindow): boolean {
  return window.startMinute === 0 && window.durationMinutes >= MINUTES_PER_DAY;
}

export function formatDisplayWindow(
  window: RuleWindow,
  languageCode: Screen0LanguageCode = "en",
): string {
  if (isFullDayRuleWindow(window)) {
    return getTranslations(languageCode).format.allDay;
  }
  const endMinute = window.startMinute + window.durationMinutes;
  return `${formatDisplayMinuteOfDay(window.startMinute)}–${formatDisplayMinuteOfDay(endMinute)}`;
}

/** Smart Block hour ranges: startHour inclusive, endHour exclusive (1–24). */
export function formatDisplayHourRange(
  startHour: number,
  endHour: number,
  languageCode: Screen0LanguageCode = "en",
): string {
  if (startHour === 0 && endHour >= 24) {
    return getTranslations(languageCode).format.allDay;
  }
  return `${formatDisplayMinuteOfDay(startHour * 60)}–${formatDisplayMinuteOfDay(endHour * 60)}`;
}

function formatDurationHoursOnly(hours: number, languageCode: Screen0LanguageCode): string {
  const t = getTranslations(languageCode).format;
  if (hours === 1) {
    return t.durationOneHour;
  }
  return formatTemplate(t.durationHoursOnly, { hours });
}

export function formatDurationMinutes(
  durationMinutes: number,
  languageCode: Screen0LanguageCode = "en",
): string {
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  const t = getTranslations(languageCode).format;
  if (hours <= 0) {
    return formatTemplate(t.durationMinutes, { minutes });
  }
  if (minutes === 0) {
    return formatDurationHoursOnly(hours, languageCode);
  }
  return formatTemplate(t.durationHoursMinutes, { hours, minutes });
}

export function formatDuration(
  totalSeconds: number,
  languageCode: Screen0LanguageCode = "en",
) {
  const totalMinutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const t = getTranslations(languageCode).format;
  if (hours <= 0) {
    return formatTemplate(t.durationMinutes, { minutes });
  }
  if (minutes === 0) {
    return formatDurationHoursOnly(hours, languageCode);
  }
  return formatTemplate(t.durationHoursMinutes, { hours, minutes });
}

export type InsightTimeBandKey = "morning" | "afternoon" | "evening" | "night";

const TIME_BAND_LABEL_KEYS: Record<
  InsightTimeBandKey,
  "timeBandMorning" | "timeBandAfternoon" | "timeBandEvening" | "timeBandNight"
> = {
  morning: "timeBandMorning",
  afternoon: "timeBandAfternoon",
  evening: "timeBandEvening",
  night: "timeBandNight",
};

export function formatInsightTimeBandLabel(
  bandKey: InsightTimeBandKey,
  languageCode: Screen0LanguageCode = "en",
) {
  const t = getTranslations(languageCode).format;
  return t[TIME_BAND_LABEL_KEYS[bandKey]];
}

/** Formats averaged activation counts for chart labels (max one decimal). */
export function formatAveragedActivationCount(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function formatActivationCountForDisplay(
  value: number,
  mode: "integer" | "averaged" = "integer",
): string {
  if (mode === "averaged") {
    return formatAveragedActivationCount(value);
  }
  return String(Math.round(value));
}

export function formatActivationCount(
  count: number,
  languageCode: Screen0LanguageCode = "en",
) {
  const t = getTranslations(languageCode).format;
  const rounded = Math.round(count);
  const word = rounded === 1 ? t.activationSingular : t.activationPlural;
  return `${rounded} ${word}`;
}

/** Local calendar date as YYYY-MM-DD (matches native iOS snapshot date). */
export function todayKey(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const year = parts.find((p) => p.type === "year")?.value ?? "0000";
  const month = parts.find((p) => p.type === "month")?.value ?? "01";
  const day = parts.find((p) => p.type === "day")?.value ?? "01";
  return `${year}-${month}-${day}`;
}

export function formatDateLabel(
  date = new Date(),
  options?: { includeYear?: boolean; languageCode?: Screen0LanguageCode },
) {
  const locale = options?.languageCode
    ? getScreen0IntlLocale(options.languageCode)
    : undefined;
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(options?.includeYear ? { year: "numeric" } : {}),
  }).format(date);
}

export function formatWeekRangeLabel(
  startKey: string,
  endKey: string,
  options?: { includeYear?: boolean; languageCode?: Screen0LanguageCode },
) {
  const locale = options?.languageCode
    ? getScreen0IntlLocale(options.languageCode)
    : undefined;
  return formatWeekRangeLabelForLocale(startKey, endKey, locale, options);
}

/** Full month + year, e.g. "June 2026". */
export function formatMonthLabel(
  key: string,
  options?: { includeYear?: boolean; languageCode?: Screen0LanguageCode },
) {
  const locale = options?.languageCode
    ? getScreen0IntlLocale(options.languageCode)
    : undefined;
  return formatMonthLabelForLocale(key, locale, options);
}

/** Locale-aware month + year for the Today tab top bar. */
export function formatEnglishMonthLabel(
  key: string,
  options?: { includeYear?: boolean; languageCode?: Screen0LanguageCode },
) {
  const locale = options?.languageCode
    ? getScreen0IntlLocale(options.languageCode)
    : "en-US";
  return formatMonthLabelForLocale(key, locale, options);
}

/** Locale-aware labels for the Today tab top bar. */
export function formatEnglishDateLabel(
  date: Date,
  options?: { includeYear?: boolean; languageCode?: Screen0LanguageCode },
) {
  const locale = options?.languageCode
    ? getScreen0IntlLocale(options.languageCode)
    : "en-US";
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(options?.includeYear ? { year: "numeric" } : {}),
  }).format(date);
}

export function formatEnglishWeekRangeLabel(
  startKey: string,
  endKey: string,
  options?: { includeYear?: boolean; languageCode?: Screen0LanguageCode },
) {
  const locale = options?.languageCode
    ? getScreen0IntlLocale(options.languageCode)
    : "en-US";
  return formatWeekRangeLabelForLocale(startKey, endKey, locale, options);
}

function formatMonthLabelForLocale(
  key: string,
  locale: Intl.LocalesArgument | undefined,
  options?: { includeYear?: boolean },
) {
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    ...(options?.includeYear ?? true ? { year: "numeric" } : {}),
  }).format(parseLocalDateKey(key));
}

function formatWeekRangeLabelForLocale(
  startKey: string,
  endKey: string,
  locale: Intl.LocalesArgument | undefined,
  options?: { includeYear?: boolean },
) {
  const includeYear = options?.includeYear ?? true;
  const start = parseLocalDateKey(startKey);
  const end = parseLocalDateKey(endKey);
  const monthDay: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };

  if (!includeYear) {
    const startLabel = new Intl.DateTimeFormat(locale, monthDay).format(start);
    const endLabel = new Intl.DateTimeFormat(locale, monthDay).format(end);
    return `${startLabel} – ${endLabel}`;
  }

  const sameYear = start.getFullYear() === end.getFullYear();
  const startLabel = new Intl.DateTimeFormat(
    locale,
    sameYear ? monthDay : { ...monthDay, year: "numeric" },
  ).format(start);
  const endLabel = new Intl.DateTimeFormat(locale, { ...monthDay, year: "numeric" }).format(end);
  return `${startLabel} – ${endLabel}`;
}

function parseLocalDateKey(key: string) {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
}
